<script setup lang="ts">
import { LoaderCircle, X } from "lucide-vue-next";
import Logo from "@/assets/icons/logo.svg?component";
import CategoryIcon from "@/assets/icons/search-category.svg?component";
import ServiceIcon from "@/assets/icons/search-service.svg?component";
import CityIcon from "@/assets/icons/search-city.svg?component";
import SearchIcon from "@/assets/icons/search-field.svg?component";

type SelectOption = {
  value: string;
  label: string;
  categoryName?: string;
};

type LanguageOption = {
  id: number;
  label: string;
};

const categoryValue = defineModel<string>("categoryValue", { default: "" });
const categorySearchText = defineModel<string>("categorySearchText", {
  default: "",
});
const serviceValue = defineModel<string>("serviceValue", { default: "" });
const serviceSearchText = defineModel<string>("serviceSearchText", {
  default: "",
});
const regionValue = defineModel<string>("regionValue", { default: "" });
const regionSearchText = defineModel<string>("regionSearchText", {
  default: "",
});
const experienceMinValue = defineModel<string>("experienceMinValue", {
  default: "",
});
const experienceMaxValue = defineModel<string>("experienceMaxValue", {
  default: "",
});
const priceMinValue = defineModel<string>("priceMinValue", { default: "" });
const priceMaxValue = defineModel<string>("priceMaxValue", { default: "" });
const languageIds = defineModel<number[]>("languageIds", { default: () => [] });

const {
  categoryOptions = [],
  serviceOptions = [],
  regionOptions = [],
  languageOptions = [],
  selectedLanguageText = "",
  showPriceFilter = false,
  loading = false,
} = defineProps<{
  categoryOptions?: SelectOption[];
  serviceOptions?: SelectOption[];
  regionOptions?: SelectOption[];
  languageOptions?: LanguageOption[];
  selectedLanguageText?: string;
  showPriceFilter?: boolean;
  loading?: boolean;
}>();

const emit = defineEmits<{
  submit: [];
  reset: [];
  filter: [];
  loadServices: [];
}>();

const localeRoute = useLocaleRoute();
const { t } = useI18n();

const isMobileSearchOpen = ref(false);
const isServiceHintVisible = ref(false);
let serviceHintTimer: ReturnType<typeof setTimeout> | undefined;

const isServiceDisabled = computed(() => !categoryValue.value);
const serviceDisabledHint = computed(() =>
  t("search.filters.service.select_category_first"),
);

function showServiceDisabledHint() {
  if (!isServiceDisabled.value) return;

  isServiceHintVisible.value = true;

  if (serviceHintTimer) clearTimeout(serviceHintTimer);
  serviceHintTimer = setTimeout(() => {
    isServiceHintVisible.value = false;
  }, 2200);
}

watch(categoryValue, value => {
  if (!value) return;

  isServiceHintVisible.value = false;
  if (serviceHintTimer) clearTimeout(serviceHintTimer);
});

onBeforeUnmount(() => {
  if (serviceHintTimer) clearTimeout(serviceHintTimer);
});

function firstRowInputClass(active: boolean, first = false) {
  return [
    "group !h-full items-center gap-3 border-0 !px-4 !py-0 transition duration-300 !opacity-100",
    first ? "rounded-l-[11px] rounded-r-none" : "rounded-none",
    "focus-within:bg-primary-50/80 focus-within:ring-0",
    "[&>input]:h-full [&>input]:min-w-0 [&>input]:font-medium [&>input]:leading-[1.2]",
    active
      ? "bg-primary-50/80 [&>input]:text-primary"
      : "bg-transparent hover:bg-[#F8FAF9] [&>input]:text-app-text",
  ].join(" ");
}

function firstRowClearButtonClass(active: boolean) {
  return active
    ? "!bg-white text-primary shadow-sm hover:!bg-white"
    : "!bg-gray-100 text-app-muted hover:!bg-gray-200 hover:text-app-text";
}

function headerIconWrapClass(active: boolean) {
  return [
    "flex size-7 shrink-0 items-center justify-center transition-colors duration-300",
    active
      ? "text-primary"
      : "text-[#737B86] group-hover:text-primary group-focus-within:text-primary",
  ].join(" ");
}

const filteredServiceOptions = computed(() => {
  if (!categoryValue.value)
    return serviceOptions.filter(option => !option.value);

  return serviceOptions.filter(
    option => !option.value || option.categoryName === categoryValue.value,
  );
});

const mobileSearchLabel = computed(() => {
  return (
    serviceSearchText.value ||
    categorySearchText.value ||
    t("search.filters.service.placeholder")
  );
});

const mobileRegionLabel = computed(() => {
  return regionSearchText.value || t("search.filters.location.placeholder");
});

function submitMobileSearch() {
  isMobileSearchOpen.value = false;
  emit("submit");
}

function applyLanguages(ids: number[]) {
  languageIds.value = ids;
  emit("filter");
}

function clearLanguages() {
  languageIds.value = [];
  emit("filter");
}
</script>

<template>
  <header
    class="sticky top-0 w-full overflow-visible border-b border-app-border bg-white"
    :class="isMobileSearchOpen ? 'z-[700]' : 'z-[1000]'"
  >
    <div
      class="relative z-[1010] mx-auto flex h-[72px] w-full max-w-[1480px] items-center justify-between gap-3 overflow-visible px-4"
    >
      <div class="flex min-w-0 flex-1 items-center gap-2.5">
        <NuxtLink
          :to="localeRoute('/')"
          class="flex shrink-0 cursor-pointer items-center gap-0.5 text-primary"
          :aria-label="$t('footer.brand_alt')"
        >
          <Logo class="h-10 w-8 shrink-0" />
        </NuxtLink>

        <form
          class="hidden h-[52px] min-w-0 grid-cols-[minmax(0,195px)_minmax(0,230px)_minmax(0,185px)_124px] items-stretch overflow-visible rounded-xl border border-app-border bg-white p-0 lg:grid xl:grid-cols-[minmax(0,230px)_minmax(0,275px)_minmax(0,210px)_124px] 2xl:grid-cols-[minmax(0,250px)_minmax(0,300px)_minmax(0,225px)_130px]"
          @submit.prevent="emit('submit')"
        >
          <AppCombobox
            v-model="categoryValue"
            v-model:search-text="categorySearchText"
            class="h-full"
            :options="categoryOptions"
            :placeholder="$t('search.filters.category.title')"
            :not-found-text="$t('search.filters.not_found')"
            dropdown-min-height="min-h-[360px]"
            dropdown-max-height="max-h-[360px]"
            content-class="!left-0 !right-auto w-[270px] min-w-[270px]"
            :input-class="firstRowInputClass(Boolean(categoryValue), true)"
            :clear-button-class="
              firstRowClearButtonClass(Boolean(categoryValue))
            "
          >
            <template #icon="{ active }">
              <span :class="headerIconWrapClass(active)">
                <CategoryIcon class="size-[21px]" />
              </span>
            </template>
          </AppCombobox>

          <div
            class="group relative z-10 h-full min-w-0 border-l border-app-border"
            :class="isServiceDisabled ? 'cursor-not-allowed' : ''"
            @pointerdown="!isServiceDisabled && emit('loadServices')"
          >
            <div
              class="h-full"
              :class="isServiceDisabled ? 'pointer-events-none' : ''"
            >
              <AppCombobox
                v-model="serviceValue"
                v-model:search-text="serviceSearchText"
                class="h-full"
                :options="filteredServiceOptions"
                :placeholder="$t('search.filters.service.title')"
                :disabled="isServiceDisabled"
                :not-found-text="$t('search.filters.not_found')"
                dropdown-min-height="min-h-[360px]"
                dropdown-max-height="max-h-[360px]"
                content-class="!left-0 !right-auto w-[290px] min-w-[290px]"
                :input-class="firstRowInputClass(Boolean(serviceValue))"
                :clear-button-class="
                  firstRowClearButtonClass(Boolean(serviceValue))
                "
              >
                <template #icon="{ active }">
                  <span :class="headerIconWrapClass(active)">
                    <ServiceIcon class="size-[21px]" />
                  </span>
                </template>
              </AppCombobox>
            </div>

            <button
              v-if="isServiceDisabled"
              type="button"
              class="absolute inset-0 z-20 cursor-not-allowed"
              :aria-label="serviceDisabledHint"
              @click.stop="showServiceDisabledHint"
            />

            <span
              v-if="isServiceDisabled"
              class="pointer-events-none absolute left-1/2 top-[calc(100%+8px)] z-[650] w-max max-w-[230px] -translate-x-1/2 rounded-lg bg-app-text px-3 py-2 text-center text-xs font-medium leading-5 text-white opacity-0 shadow-lg transition-opacity duration-300 group-hover:opacity-100 group-focus-within:opacity-100"
              :class="isServiceHintVisible ? '!opacity-100' : ''"
              role="tooltip"
            >
              {{ serviceDisabledHint }}
            </span>
          </div>

          <div class="relative z-10 h-full min-w-0 border-l border-app-border">
            <AppCombobox
              v-model="regionValue"
              v-model:search-text="regionSearchText"
              class="h-full"
              :options="regionOptions"
              :placeholder="$t('search.filters.location.title')"
              :not-found-text="$t('search.filters.not_found')"
              dropdown-min-height="min-h-[360px]"
              dropdown-max-height="max-h-[360px]"
              content-class="!left-0 !right-auto w-[270px] min-w-[270px]"
              :input-class="firstRowInputClass(Boolean(regionValue))"
              :clear-button-class="
                firstRowClearButtonClass(Boolean(regionValue))
              "
            >
              <template #icon="{ active }">
                <span :class="headerIconWrapClass(active)">
                  <CityIcon class="size-[21px]" />
                </span>
              </template>
            </AppCombobox>
          </div>

          <div class="relative z-20 h-full min-w-0">
            <button
              type="submit"
              class="absolute -bottom-px -right-px -top-px left-0 flex items-center justify-center gap-2 rounded-l-none rounded-r-xl bg-primary px-4 text-sm font-semibold leading-none text-white transition-colors duration-300 hover:bg-primary-400 disabled:cursor-wait disabled:opacity-90"
              :aria-label="$t('hero.search.action')"
              :aria-busy="loading"
              :disabled="loading"
            >
              <LoaderCircle
                v-if="loading"
                class="size-[18px] shrink-0 animate-spin"
              />
              <SearchIcon v-else class="size-[19px] shrink-0" />
              <span class="leading-none">{{ $t("hero.search.action") }}</span>
            </button>
          </div>
        </form>

        <button
          type="button"
          class="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl border border-app-border bg-white px-3 text-left transition duration-300 hover:border-gray-400 lg:hidden"
          @click="isMobileSearchOpen = true"
        >
          <LoaderCircle
            v-if="loading"
            class="size-4 shrink-0 animate-spin text-primary"
          />
          <SearchIcon v-else class="size-4 shrink-0 text-app-text" />
          <span
            class="min-w-0 flex-1 truncate text-sm font-semibold text-app-text"
          >
            {{ mobileSearchLabel }}
          </span>
          <span
            class="hidden max-w-[120px] truncate border-l border-app-border pl-2 text-xs font-medium text-app-muted sm:block"
          >
            {{ mobileRegionLabel }}
          </span>
        </button>
      </div>

      <HeaderActions />
    </div>

    <div class="border-t border-app-border bg-white">
      <div
        class="search-filter-carousel relative z-[280] mx-auto flex w-full max-w-[1480px] flex-nowrap items-center gap-2 overflow-x-auto px-4 py-2.5 md:overflow-visible"
        data-scroll-lock-ignore
      >
        <WorkersRangeFilterDropdown
          v-model:min-value="experienceMinValue"
          v-model:max-value="experienceMaxValue"
          :title="$t('search.filters.experience.title')"
          :min-label="$t('search.filters.experience.min')"
          :max-label="$t('search.filters.experience.max')"
          :apply-text="$t('search.filters.experience.apply')"
          :reset-text="$t('search.filters.experience.reset')"
          :min="0"
          :max="60"
          :step="1"
          :suffix="$t('search.filters.experience.unit_short')"
          @apply="emit('filter')"
        />

        <WorkersRangeFilterDropdown
          v-if="showPriceFilter"
          v-model:min-value="priceMinValue"
          v-model:max-value="priceMaxValue"
          :title="$t('search.filters.price.title')"
          :min-label="$t('search.filters.price.min')"
          :max-label="$t('search.filters.price.max')"
          :apply-text="$t('search.filters.price.apply')"
          :reset-text="$t('search.filters.price.reset')"
          :min="0"
          :max="999999"
          :step="1"
          suffix="€"
          @apply="emit('filter')"
        />

        <div class="w-[192px] shrink-0">
          <AppMultiSelect
            :options="languageOptions"
            :selected-ids="languageIds"
            :placeholder="$t('search.filters.language.any')"
            :empty-text="$t('search.filters.not_found')"
            :selected-text="selectedLanguageText"
            :search-placeholder="$t('search.filters.language.placeholder')"
            :sheet-title="$t('search.filters.language.title')"
            :apply-text="$t('search.filters.language.apply')"
            :show-chips="false"
            trigger-class="!h-11 !rounded-xl px-3.5 md:hover:bg-primary-50"
            active-trigger-class="!border-primary/35 !bg-primary-50/80 !text-primary md:hover:!bg-primary-100/70"
            inactive-trigger-class="!border-app-border !bg-white text-app-text hover:!border-primary/35 hover:!bg-primary-50 hover:!text-primary"
            clear-button-class="!bg-white text-primary shadow-sm hover:!bg-white"
            @clear="clearLanguages"
            @apply="applyLanguages"
          />
        </div>

        <button
          type="button"
          class="flex h-11 min-w-[132px] shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border border-app-border bg-white px-4 text-sm font-semibold text-app-muted transition duration-300 hover:border-primary/35 hover:bg-primary-50 hover:text-primary"
          @click="emit('reset')"
        >
          <X class="size-3.5" />
          {{ $t("search.filters.reset") }}
        </button>
      </div>
    </div>

    <Teleport to="body">
      <Transition name="workers-search-mobile-backdrop">
        <button
          v-if="isMobileSearchOpen"
          type="button"
          class="fixed inset-0 z-[800] bg-black/45 lg:hidden"
          aria-label="Close search"
          @click="isMobileSearchOpen = false"
        />
      </Transition>

      <Transition name="workers-search-mobile-panel">
        <section
          v-if="isMobileSearchOpen"
          class="fixed inset-0 z-[810] min-h-[100dvh] overflow-y-auto overscroll-contain bg-white px-4 pb-6 pt-4 lg:hidden"
        >
          <div
            class="sticky top-0 z-10 mx-auto mb-5 flex max-w-[420px] items-center justify-between gap-4 border-b border-app-border bg-white pb-4"
          >
            <Logo class="h-9 w-8 shrink-0 text-primary" />
            <button
              type="button"
              class="flex size-10 items-center justify-center rounded-full text-app-text transition duration-300 hover:bg-gray-100"
              @click="isMobileSearchOpen = false"
            >
              <X class="size-5" />
            </button>
          </div>

          <form
            class="mx-auto max-w-[420px] rounded-2xl border border-app-border bg-white p-3"
            @submit.prevent="submitMobileSearch"
          >
            <AppCombobox
              v-model="categoryValue"
              v-model:search-text="categorySearchText"
              :options="categoryOptions"
              :placeholder="$t('search.filters.category.title')"
              :not-found-text="$t('search.filters.not_found')"
              dropdown-min-height="min-h-[360px]"
              dropdown-max-height="max-h-[260px]"
              input-class="h-12 rounded-none border-0 bg-transparent px-1 py-0 focus-within:bg-transparent"
              :mobile-action-text="$t('hero.search.action')"
            >
              <template #icon="{ active }">
                <span :class="headerIconWrapClass(active)">
                  <CategoryIcon class="size-[21px]" />
                </span>
              </template>
            </AppCombobox>

            <div
              class="group relative border-t border-app-border"
              :class="isServiceDisabled ? 'cursor-not-allowed' : ''"
              @pointerdown="!isServiceDisabled && emit('loadServices')"
            >
              <div :class="isServiceDisabled ? 'pointer-events-none' : ''">
                <AppCombobox
                  v-model="serviceValue"
                  v-model:search-text="serviceSearchText"
                  :options="filteredServiceOptions"
                  :placeholder="$t('search.filters.service.title')"
                  :disabled="isServiceDisabled"
                  :not-found-text="$t('search.filters.not_found')"
                  dropdown-min-height="min-h-[360px]"
                  dropdown-max-height="max-h-[260px]"
                  input-class="h-12 rounded-none border-0 bg-transparent px-1 py-0 focus-within:bg-transparent"
                  :mobile-action-text="$t('hero.search.action')"
                >
                  <template #icon="{ active }">
                    <span :class="headerIconWrapClass(active)">
                      <ServiceIcon class="size-[21px]" />
                    </span>
                  </template>
                </AppCombobox>
              </div>

              <button
                v-if="isServiceDisabled"
                type="button"
                class="absolute inset-0 z-20 cursor-not-allowed"
                :aria-label="serviceDisabledHint"
                @click.stop="showServiceDisabledHint"
              />

              <span
                v-if="isServiceDisabled && isServiceHintVisible"
                class="pointer-events-none absolute left-1/2 top-[calc(100%-2px)] z-30 w-max max-w-[260px] -translate-x-1/2 rounded-lg bg-app-text px-3 py-2 text-center text-xs font-medium leading-5 text-white shadow-lg"
                role="tooltip"
              >
                {{ serviceDisabledHint }}
              </span>
            </div>

            <div class="border-t border-app-border">
              <AppCombobox
                v-model="regionValue"
                v-model:search-text="regionSearchText"
                :options="regionOptions"
                :placeholder="$t('search.filters.location.title')"
                :not-found-text="$t('search.filters.not_found')"
                dropdown-min-height="min-h-[360px]"
                dropdown-max-height="max-h-[260px]"
                input-class="h-12 rounded-none border-0 bg-transparent px-1 py-0 focus-within:bg-transparent"
                :mobile-action-text="$t('hero.search.action')"
              >
                <template #icon="{ active }">
                  <span :class="headerIconWrapClass(active)">
                    <CityIcon class="size-[21px]" />
                  </span>
                </template>
              </AppCombobox>
            </div>

            <button
              type="submit"
              class="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-base font-bold text-white transition duration-300 hover:bg-primary-400 disabled:cursor-wait disabled:opacity-90"
              :aria-busy="loading"
              :disabled="loading"
            >
              {{ $t("hero.search.action") }}
              <LoaderCircle v-if="loading" class="size-6 animate-spin" />
              <SearchIcon v-else class="size-6" />
            </button>
          </form>
        </section>
      </Transition>
    </Teleport>
  </header>
</template>

<style scoped>
.workers-search-mobile-backdrop-enter-active,
.workers-search-mobile-backdrop-leave-active {
  transition: opacity 160ms ease;
}

.workers-search-mobile-backdrop-enter-from,
.workers-search-mobile-backdrop-leave-to {
  opacity: 0;
}

.workers-search-mobile-panel-enter-active,
.workers-search-mobile-panel-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.workers-search-mobile-panel-enter-from,
.workers-search-mobile-panel-leave-to {
  opacity: 0;
  transform: translateY(-8px) scale(0.98);
}
.search-filter-carousel {
  scrollbar-width: none;
}

.search-filter-carousel::-webkit-scrollbar {
  display: none;
}
</style>
